import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Box,
  Card,
  FormControlLabel,
  Switch,
  Table,
  TableBody,
  TableContainer,
  TablePagination,
} from '@mui/material';
import { Scrollbar } from '@/components';
import { SkeletonLoadingTable } from '@/components/skeleton';
import { TableEmptyRows, TableHeadCustom, TableNoData } from '@/components/table';
import TransactionsTableRow from '@/features/transactions/components/TransactionsTableRow';
import TransactionsTableToolbar from '@/features/transactions/components/TransactionsTableToolbar';
import {
  TRANSACTIONS_TABLE_HEAD,
  buildTransactionSourceOptions,
  buildTransactionStatusOptions,
  buildTransactionTypeOptions,
  isFiltersApplied,
} from '@/features/transactions/utils';
import { useSnackbarMsg } from '@/hooks/useSnackbarMsg';
import useTable from '@/hooks/useTable';
import { getUserTransactions } from '@/features/users/shared/api';
import { PaymentTransaction, TransactionFilters, TransactionOption } from '@/features/transactions/types';

const defaultFilters: TransactionFilters = {
  query: '',
  status: '',
  type: '',
  source: '',
};

export default function ClientTransactions() {
  const table = useTable({
    defaultOrder: 'desc',
    defaultOrderBy: 'created_at',
  });
  const { userId } = useParams();
  const { errorMsg } = useSnackbarMsg();
  const [filters, setFilters] = useState<TransactionFilters>(defaultFilters);

  const { data, isLoading } = useQuery({
    queryKey: [
      'clientTransactions',
      userId,
      table.page,
      table.rowsPerPage,
      table.order,
      table.orderBy,
      filters.status,
      filters.type,
      filters.source,
      filters.query,
    ],
    queryFn: () =>
      getUserTransactions(Number(userId), {
        page: table.page + 1,
        limit: table.rowsPerPage,
        status: filters.status !== '' ? String(filters.status) : undefined,
        type: filters.type !== '' ? String(filters.type) : undefined,
        source: filters.source || undefined,
        search: filters.query || undefined,
        sortBy: table.orderBy,
        sortOrder: table.order,
      }),
    enabled: !!userId,
    onError: (error) => {
      errorMsg(error, 'Unable to load transactions');
    },
  });

  const transactions = data?.data ?? [];
  const meta =
    data?.meta ??
    ({
      page: 1,
      limit: transactions.length || table.rowsPerPage,
      total: transactions.length,
      totalPages: 1,
    } as const);

  const statusOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionStatusOptions(transactions);
  }, [transactions]);

  const typeOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionTypeOptions(transactions);
  }, [transactions]);

  const sourceOptions = useMemo<TransactionOption[]>(() => {
    return buildTransactionSourceOptions(transactions);
  }, [transactions]);

  const handleFilters = (name: keyof TransactionFilters, value: string) => {
    setFilters((prev) => ({ ...prev, [name]: value }));
    table.setPage(0);
  };

  const handleResetFilters = () => {
    setFilters(defaultFilters);
    table.setPage(0);
  };

  const dataFiltered = transactions;
  const dataInPage = dataFiltered;
  const denseHeight = table.dense ? 52 : 72;
  const isFiltered = isFiltersApplied(filters);
  const isNotFound = !dataFiltered.length;

  return (
    <Card>
      {isLoading ? (
        <SkeletonLoadingTable />
      ) : (
        <>
          <TransactionsTableToolbar
            filters={filters}
            onFilters={handleFilters}
            onResetFilters={handleResetFilters}
            statusOptions={statusOptions}
            typeOptions={typeOptions}
            sourceOptions={sourceOptions}
            isFiltered={isFiltered}
          />

          <Scrollbar>
            <TableContainer sx={{ minWidth: 1000 }}>
              <Table size={table.dense ? 'small' : 'medium'}>
                <TableHeadCustom
                  order={table.order}
                  orderBy={table.orderBy}
                  headLabel={TRANSACTIONS_TABLE_HEAD}
                  rowCount={dataFiltered.length}
                  numSelected={table.selected.length}
                  onSort={table.onSort}
                />
                <TableBody>
                  {dataInPage.map((row: PaymentTransaction) => (
                    <TransactionsTableRow key={row.id} row={row} />
                  ))}

                  {dataInPage.length < table.rowsPerPage && (
                    <TableEmptyRows
                      height={denseHeight}
                      emptyRows={table.rowsPerPage - dataInPage.length}
                    />
                  )}

                  <TableNoData
                    isNotFound={isNotFound}
                    title={
                      isNotFound
                        ? isFiltered
                          ? 'No transactions found'
                          : 'No transactions available'
                        : undefined
                    }
                  />
                </TableBody>
              </Table>
            </TableContainer>
          </Scrollbar>

          <Box sx={{ position: 'relative' }}>
            {(meta?.total ?? 0) > table.rowsPerPage && (
              <TablePagination
                rowsPerPageOptions={[10, 25, 50]}
                component="div"
                count={meta?.total ?? 0}
                rowsPerPage={table.rowsPerPage}
                page={table.page}
                onPageChange={table.onChangePage}
                onRowsPerPageChange={table.onChangeRowsPerPage}
              />
            )}

            <FormControlLabel
              control={<Switch checked={table.dense} onChange={table.onChangeDense} />}
              label="Dense"
              sx={{ px: 3, py: 1.5, top: 0, position: { md: 'absolute' } }}
            />
          </Box>
        </>
      )}
    </Card>
  );
}
